import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Channel, Program } from '@shared/types.ts';
import { useChannels, useEpg, useGuide, guideBucket, GUIDE_SPAN_MS } from '@/lib/queries.ts';
import { player } from '@/lib/player.ts';
import { usePlayer } from '@/stores/playback.ts';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';
import { useLineup } from '@/stores/lineup.ts';
import { ChannelMenu } from '@/features/channels/ChannelMenu.tsx';
import { useUi } from '@/stores/ui.ts';
import { ChannelLogo } from '@/ui/ChannelLogo.tsx';
import {
  PX_PER_MS, CHANNEL_COL_PX, ROW_HEIGHT_PX,
  toBlocks, blockAt, ticks, scrollToShow, clockLabel, slotLabel, type Block,
} from './geometry.ts';

/**
 * The channel guide, built to feel like a cable box.
 *
 * WHAT MAKES IT FEEL RIGHT, and each of these is a decision rather than an
 * accident:
 *
 *  - MOVING UP OR DOWN KEEPS YOUR PLACE IN TIME, not your place in a list.
 *    Change channel at 8:40 and you land on whatever that channel is showing
 *    at 8:40. This is the single thing that separates a guide from a table.
 *  - The grid does NOT use the app's DOM focus system. There are thousands of
 *    cells; giving each one a tabindex and moving real focus between them
 *    would cost a render and a scroll calculation per press. The guide owns a
 *    cursor, draws the highlight itself, and takes keys from a layer pushed on
 *    top of spatial navigation (lib/keys.ts).
 *  - Only the visible rows are rendered. A hundred channels of absolutely
 *    positioned blocks is more DOM than a Fire Stick will hold at 60fps.
 *  - The viewport moves as little as it can. A guide that recentres on every
 *    press feels like it is fighting you.
 */

/** Rows drawn beyond the viewport, so a fast scroll never shows a blank band. */
const OVERSCAN = 3;

/** The guide's full width in pixels — the whole window, not what is on screen. */
const TIMELINE_PX = GUIDE_SPAN_MS * PX_PER_MS;

/**
 * Keep a horizontal offset inside the timeline.
 *
 * Needed now that something other than the cursor moves it: `scrollToShow`
 * could only ever land on a programme that exists, so it clamped itself. A
 * wheel has no such manners and will happily pan a day past the last block,
 * leaving an empty grid and no clue which way is back.
 */
function clampX(x: number, viewportW: number): number {
  const lane = Math.max(0, viewportW - CHANNEL_COL_PX);
  return Math.max(0, Math.min(x, Math.max(0, TIMELINE_PX - lane)));
}

export function GuidePage() {
  // Same lineup as the grid: hidden channels are filtered server-side, so the
  // guide cannot drift from what the viewer chose to see.
  const { data: channels } = useChannels('tv');
  const { data: guide, isLoading } = useGuide('tv');
  const setLineup = useLineup((s) => s.set);
  const setView = useUi((s) => s.setView);

  const originMs = guideBucket();
  const viewportRef = useRef<HTMLDivElement>(null);

  const [cursor, setCursor] = useState({ row: 0, col: 0 });
  const [cursorTimeMs, setCursorTimeMs] = useState(() => Date.now());
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [viewport, setViewport] = useState({ w: 1280, h: 540 });
  const [nowMs, setNowMs] = useState(() => Date.now());

  const rows = useMemo(() => {
    if (!channels || !guide) return [] as Array<{ channel: Channel; blocks: Block[] }>;
    const byId = new Map(guide.rows.map((r) => [r.channelId, r.programs]));
    return channels.map((channel) => ({
      channel,
      blocks: toBlocks(byId.get(channel.id) ?? [], originMs),
    }));
  }, [channels, guide, originMs]);

  const current = rows[cursor.row]?.blocks[cursor.col];
  const playingChannel = usePlayer().channel;

  // The clock hand, and the only thing here on a timer. A minute is as
  // precise as a guide needs and costs one render an hour of viewing.
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    /*
     * ONE MEASUREMENT DECIDES HOW MANY ROWS EXIST, so a bad one has to be
     * survivable.
     *
     * The guide was found showing three channels with the bottom of the
     * screen blank, scrolling three at a time, and no way back. Nothing in
     * the rendering is wrong for that to happen — it is what this code does
     * when `h` is small, and `h` can be small for reasons that have nothing
     * to do with the guide: a WebView the system suspended and resumed, a
     * route mid-swap, a layout that had not settled when the observer first
     * fired.
     *
     * A floor turns "unrecoverable" into "renders a few rows more than it
     * needs to", which nobody will ever notice. Over-rendering a virtualised
     * list is a performance question; under-rendering it is a broken screen.
     */
    const measure = () => {
      const h = el.clientHeight;
      // Say so when the floor is used. If the guide ever looks wrong again,
      // this line is the difference between knowing the measurement was the
      // cause and starting the search over.
      if (h <= ROW_HEIGHT_PX * 2) {
        console.warn(`[n8m] guide measured ${h}px tall — using the floor instead`);
      }
      setViewport({
        w: el.clientWidth || window.innerWidth,
        // Below a couple of rows the number is not a viewport, it is a
        // mistake — fall back to something that is at least the right order.
        h: h > ROW_HEIGHT_PX * 2 ? h : Math.max(window.innerHeight * 0.6, ROW_HEIGHT_PX * 6),
      });
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    // A resumed WebView re-lays-out without necessarily changing this
    // element's size, so the observer alone can miss it.
    document.addEventListener('visibilitychange', measure);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      document.removeEventListener('visibilitychange', measure);
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Open on what is on NOW, which is what a guide is for.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || rows.length === 0) return;
    seeded.current = true;
    const playingId = player.getState().channel?.id;
    const row = Math.max(0, rows.findIndex((r) => r.channel.id === playingId));
    const col = Math.max(0, blockAt(rows[row].blocks, Date.now()));
    setCursor({ row, col });
    setCursorTimeMs(Date.now());
  }, [rows]);

  /**
   * Follow the cursor with as little movement as possible.
   *
   * THE TWO AXES ARE NOT SYMMETRICAL ANY MORE, and that is deliberate.
   *
   * Vertically the grid is now a real scroll container, so this ASSIGNS
   * `scrollTop` rather than setting state: the browser owns the position and
   * `offsetY` is a mirror of it, kept for virtualisation. Writing state
   * instead would give two authorities over one number, and the one the
   * wheel moves would be immediately overruled by the one the cursor moves.
   *
   * Horizontally it stays a transform, because the channel column has to
   * stay put while the lanes move. Native overflow on this axis would need
   * one scroll container per row kept in sync, or a sticky column layout —
   * both more moving parts than a single shared translate.
   */
  useEffect(() => {
    if (!current) return;
    setOffsetX((x) => clampX(
      scrollToShow(x, viewport.w - CHANNEL_COL_PX, current.left, current.left + current.width, 24),
      viewport.w,
    ));

    const el = viewportRef.current;
    if (!el) return;
    const height = el.clientHeight || viewport.h;
    const want = scrollToShow(
      el.scrollTop, height,
      cursor.row * ROW_HEIGHT_PX, (cursor.row + 1) * ROW_HEIGHT_PX,
      ROW_HEIGHT_PX,
    );
    // Sub-pixel churn would fight a trackpad mid-gesture.
    if (Math.abs(want - el.scrollTop) > 1) el.scrollTop = want;
  }, [current, cursor.row, viewport.w, viewport.h]);

  /**
   * THE WHEEL, which this screen never had an answer for.
   *
   * Vertical needs no handler at all now — the viewport scrolls itself and
   * `onScroll` mirrors the position back. This is only the horizontal axis,
   * where there is no scrollbar for a browser to drive.
   *
   * ATTACHED BY HAND, NOT VIA onWheel. React registers its root wheel
   * listener as PASSIVE, so `preventDefault` inside a React onWheel handler
   * does nothing — the gesture still reaches the browser, and on a trackpad a
   * horizontal swipe there is the back-navigation gesture. A listener
   * registered with `{ passive: false }` is the only way to take it.
   */
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      // Shift+wheel is the conventional "scroll sideways"; a trackpad sends
      // deltaX directly. Anything else is vertical and belongs to the browser.
      const sideways = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!sideways) return;
      e.preventDefault();
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      setOffsetX((x) => clampX(x + delta, el.clientWidth || window.innerWidth));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  /**
   * Tune, and STAY on the guide route.
   *
   * Navigating to /live was wrong: it made Back from the player drop you on
   * the channel grid, which is not where you came from. Theater hides the
   * grid behind the video anyway, so staying here costs nothing and Back
   * steps down one tier straight back into the guide — the cable-box
   * behaviour, where the guide is a thing you look at over the picture
   * rather than a place you leave.
   *
   * The player pushes its own key layer while immersive (see App), and that
   * layer sits above this one, so the remote belongs to playback until Back
   * brings the guide forward again.
   */
  const tune = useCallback((channel: Channel) => {
    setLineup((channels ?? []).slice());
    setView('theater');
    void player.play(channel).catch(() => { /* surfaced in player state */ });
  }, [channels, setLineup, setView]);

  /*
   * The guide's own key layer, on top of spatial navigation.
   *
   * Registered ONCE and reading the cursor through refs. Re-registering on
   * every keypress — which listing the cursor in the deps would do — churns
   * the layer stack for no reason and is a good way to end up acting on a
   * stale closure.
   */
  // null means closed; a channel (or an explicit null inside the dialog)
  // means open — hence the separate flag rather than overloading the value.
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuChannelValue, setMenuChannelValue] = useState<Channel | null>(null);
  const setMenuChannel = (c: Channel | null) => { setMenuChannelValue(c); setMenuOpen(true); };

  const state = useRef({ rows, cursor, cursorTimeMs });
  state.current = { rows, cursor, cursorTimeMs };

  /*
   * A MOUSE AND A FINGER, which this screen had no answer for at all.
   *
   * The guide keeps its own cursor and takes keys from a pushed layer — that
   * is right for a remote and it quietly made the whole grid inert to every
   * other input device. Nothing here had an onClick, so on a desktop browser
   * Live TV could be READ but not used, and on a phone a tap did nothing
   * whatsoever. It was only ever finished for the living room.
   *
   * The rule matches the OK key exactly rather than inventing a second one:
   * point at a programme and the cursor goes there; if that programme is on
   * NOW, it also tunes. Clicking something at nine o'clock tonight selects it
   * and shows what it is, because there is nothing to watch yet and a guide
   * that changes channel while you read ahead is a guide people stop
   * trusting.
   */
  const pick = useCallback((row: number, col: number) => {
    const r = state.current.rows;
    const block = r[row]?.blocks[col];
    if (!block) return;
    setCursor({ row, col });
    setCursorTimeMs(block.startMs);
    const now = Date.now();
    if (block.startMs <= now && block.endMs > now) tuneRef.current(r[row].channel);
  }, []);

  const tuneRef = useRef(tune);
  tuneRef.current = tune;

  useEffect(() => {
    // Nothing in the guide is DOM-focused; drop whatever was, so the base
    // layer has nothing to act on and no stray ring is left on screen.
    (document.activeElement as HTMLElement | null)?.blur?.();

    return pushKeyLayer((key: NavKey) => {
      /*
       * BACK IS NOT OURS ANY MORE.
       *
       * It used to navigate to /live, which meant "leave the guide for the
       * channel grid" — and now that the two are one screen, that was a
       * navigation to the page you are already on: a key that visibly did
       * nothing. The guide is a top-level screen like any other, so BACK
       * falls through to the wrapper, which pops or exits exactly as it does
       * from the tile grid.
       *
       * Stepping down out of theater is not affected: the player pushes its
       * own layer above this one while immersive, and that layer takes BACK
       * before this ever sees it.
       */

      /*
       * MENU, on the channel the cursor is on.
       *
       * The same dialog the tile grid opens, which is the point: star, hide
       * and — since the two views merged — the switch back to tiles all live
       * in one place whichever way you are looking at the lineup. Without
       * this the guide would be a screen you could enter and never change
       * view from again.
       */
      if (key === 'menu') {
        const { rows: r, cursor: c } = state.current;
        setMenuChannel(r[c.row]?.channel ?? null);
        return true;
      }

      /*
       * The nav bar is still reachable, and that matters on a remote: a grid
       * that swallows every key is a grid you cannot get out of except by
       * going back. Up from the top row hands the press to spatial
       * navigation, which lands on the nav links; down from there returns.
       */
      const active = document.activeElement as HTMLElement | null;
      if (active?.matches?.('[data-nav]')) {
        if (key === 'down') { active.blur(); return true; }
        return false;   // the nav owns left/right/select while it has focus
      }

      const { rows: r, cursor: c, cursorTimeMs: at } = state.current;
      if (r.length === 0) return true;
      if (key === 'up' && c.row === 0) return false;   // out to the nav

      if (key === 'select') {
        const block = r[c.row]?.blocks[c.col];
        const now = Date.now();
        /*
         * On a programme that is on NOW, OK tunes — the cable-box reflex.
         * On anything else it does nothing: the panel already says what it
         * is, and a guide that changes channel while you are reading ahead
         * is a guide people stop trusting.
         */
        if (block && block.startMs <= now && block.endMs > now) {
          tuneRef.current(r[c.row].channel);
        }
        return true;
      }

      if (key === 'left' || key === 'right') {
        const blocks = r[c.row]?.blocks ?? [];
        const col = key === 'left'
          ? Math.max(0, c.col - 1)
          : Math.min(blocks.length - 1, c.col + 1);
        const block = blocks[col];
        if (block) setCursorTimeMs(block.startMs);
        setCursor({ row: c.row, col });
        return true;
      }

      if (key === 'up' || key === 'down') {
        const row = key === 'up'
          ? Math.max(0, c.row - 1)
          : Math.min(r.length - 1, c.row + 1);
        /*
         * KEEP THE TIME, not the index. Landing on the Nth programme of the
         * new channel would be meaningless; landing on whatever is on at the
         * same moment is the whole idea of a guide.
         */
        setCursor({ row, col: Math.max(0, blockAt(r[row].blocks, at)) });
        return true;
      }

      return true;
    });
  }, []);

  if (isLoading && rows.length === 0) {
    return <div className="center muted">Loading the guide…</div>;
  }
  if (rows.length === 0) {
    return <div className="center muted">No guide data for these channels.</div>;
  }

  const firstRow = Math.max(0, Math.floor(offsetY / ROW_HEIGHT_PX) - OVERSCAN);
  const lastRow = Math.min(rows.length, Math.ceil((offsetY + viewport.h) / ROW_HEIGHT_PX) + OVERSCAN);
  const visible = rows.slice(firstRow, lastRow);
  const nowLeft = (nowMs - originMs) * PX_PER_MS;

  return (
    <div className="guide">
      <GuideDetail
        channel={rows[cursor.row]?.channel}
        block={current}
        nowMs={nowMs}
        playing={playingChannel}
      />

      <div className="guide-timebar">
        <div className="guide-corner">{new Date(originMs).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</div>
        <div className="guide-times">
          <div className="guide-track" style={{ transform: `translateX(${-offsetX}px)` }}>
            {ticks(originMs, GUIDE_SPAN_MS).map((t) => (
              <div key={t} className="guide-tick" style={{ left: (t - originMs) * PX_PER_MS }}>
                {clockLabel(t)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/*
        A REAL SCROLL CONTAINER, which it was not before.

        The rows used to be moved by a transform driven entirely by the
        cursor, so the only thing that could scroll this grid was a remote.
        A wheel had nothing to act on, there was no scrollbar to drag, and a
        finger did nothing — the guide was readable in a browser and stuck on
        whatever happened to be on screen.

        Giving the browser the vertical axis buys the wheel, the scrollbar,
        trackpad and touch momentum, Page Up/Down and Home/End, all for free
        and all behaving the way they do everywhere else. `offsetY` is now
        read back OUT of it, purely so virtualisation knows which rows to draw.
      */}
      <div
        className="guide-viewport"
        ref={viewportRef}
        onScroll={(e) => {
          const top = e.currentTarget.scrollTop;
          // Whole pixels only: a trackpad emits fractions by the hundred and
          // each one would be a re-render of every visible row.
          setOffsetY((y) => (Math.abs(y - top) >= 1 ? top : y));
        }}
      >
        {/* Gives the container its true height, so the rows we do not render
            still occupy the space they would. */}
        <div className="guide-rows" style={{ height: rows.length * ROW_HEIGHT_PX }}>
          {visible.map(({ channel, blocks }, i) => {
            const index = firstRow + i;
            return (
              <div
                key={channel.id}
                className={`guide-row ${index === cursor.row ? 'on-row' : ''}`}
                style={{ top: index * ROW_HEIGHT_PX, height: ROW_HEIGHT_PX }}
              >
                {/* The logo is the obvious thing to point at, and it means
                    "watch this" rather than "tell me about this" — so it goes
                    straight to whatever is on now. */}
                <div
                  className="guide-chan"
                  onClick={() => pick(index, Math.max(0, blockAt(blocks, Date.now())))}
                  role="button"
                  tabIndex={-1}
                >
                  <span className="guide-num">{channel.number}</span>
                  <ChannelLogo channel={channel} className="guide-logo" />
                </div>

                <div className="guide-lane">
                  <div className="guide-track" style={{ transform: `translateX(${-offsetX}px)` }}>
                    {blocks.map((b, col) => (
                      <div
                        key={`${b.startMs}-${col}`}
                        className={[
                          'guide-block',
                          index === cursor.row && col === cursor.col ? 'selected' : '',
                          b.program.filler ? 'filler' : '',
                          b.endMs <= nowMs ? 'past' : '',
                        ].filter(Boolean).join(' ')}
                        style={{ left: b.left, width: Math.max(2, b.width - 3) }}
                        /* Deliberately NOT a Focusable: the guide owns its own
                           cursor, and a second focus system over the same grid
                           is the bug class this project has already paid for
                           twice. A plain handler moves the cursor the guide
                           already has. */
                        onClick={() => pick(index, col)}
                        role="button"
                        tabIndex={-1}
                      >
                        <span className="guide-title">{b.program.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {/* The clock hand. Sits above the blocks and moves with the grid. */}
          <div
            className="guide-now"
            style={{ left: CHANNEL_COL_PX + nowLeft - offsetX, height: rows.length * ROW_HEIGHT_PX }}
          />
        </div>
      </div>

      {/*
        The same dialog the tile grid opens. No lineup filter passed: the
        guide fetches a whole grid rather than a filtered list, so offering
        one here would be an option that does nothing — and a menu item that
        is present and inert is worse than one that is absent.
      */}
      {menuOpen && (
        <ChannelMenu channel={menuChannelValue} onClose={() => setMenuOpen(false)} />
      )}
    </div>
  );
}

/**
 * The panel above the grid.
 *
 * Titles and times come from the grid data and are instant. The DESCRIPTION
 * is fetched per channel and only for what is highlighted — descriptions are
 * most of the bytes in a guide and none of the grid, so they are not in the
 * bulk payload at all. See `Guide` in shared/types.ts.
 */
function GuideDetail({
  channel, block, nowMs, playing,
}: {
  channel?: Channel;
  block?: Block;
  nowMs: number;
  playing: Channel | null;
}) {
  const { data: programs } = useEpg(channel?.id ?? '', !!channel);

  const description = useMemo(() => {
    if (!block || !programs) return null;
    const match = programs.find((p: Program) => p.startUtc === block.program.startUtc);
    return match?.description ?? null;
  }, [block, programs]);

  const live = !!block && block.startMs <= nowMs && block.endMs > nowMs;
  const progress = live && block ? (nowMs - block.startMs) / (block.endMs - block.startMs) : 0;

  /*
   * EVERY ROW IS ALWAYS RENDERED, even when empty.
   *
   * The panel used to grow and shrink with its contents — a progress bar only
   * when live, a description only when one had loaded — and the whole grid
   * below it moved every time the cursor did. A guide that shifts under you
   * while you read it is unusable, so the panel is a fixed height and each
   * line holds its space whether or not it has anything in it.
   */
  return (
    <div className="guide-detail">
      <div className="guide-detail-text">
        <div className="guide-detail-head">
          <span className="guide-detail-title">{block?.program.title ?? ''}</span>
          {live && <span className="guide-live">ON NOW</span>}
        </div>
        <div className="guide-detail-sub">
          {channel && block
            ? `${channel.number} · ${channel.name} · ${slotLabel(block.startMs, block.endMs)}` +
              (block.program.category ? ` · ${block.program.category}` : '')
            : '\u00a0'}
        </div>
        <div className="guide-detail-bar">
          {live && <i style={{ transform: `scaleX(${progress.toFixed(3)})` }} />}
        </div>
        <div className="guide-detail-desc">
          {!block ? '' : block.program.filler ? 'No guide data for this channel.' : description ?? ''}
        </div>
      </div>

      {/*
        The preview well.
        
        It holds the SPACE; the picture itself is the app's one <video>
        element, moved here by CSS while the guide is the route (see
        styles.css). That is the same trick the corner PiP uses and the reason
        it works on the native path too — the surface follows wherever the
        page puts that element, so the preview needs no player of its own and
        nothing restarts when you open the guide.
      */}
      <div className="guide-preview">
        {playing?.kind === 'tv'
          // The <video> element is sitting over this well; leave it alone.
          ? null
          : playing
            // Radio has nothing to show, but "what is playing" still belongs
            // here — it is the only place the guide says so now that the
            // footer is out of the way.
            ? (
              <div className="guide-preview-audio">
                <ChannelLogo channel={playing} className="guide-preview-logo" />
                <span>{playing.name}</span>
              </div>
            )
            : <span className="guide-preview-idle">No channel playing</span>}
      </div>
    </div>
  );
}
